import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { MemberErasureResult } from "./models/MemberErasure";
import { Member } from "./models/Member";
import { Group } from "./models/Group";
import { LoadingStatus } from "../sessions/models/LoadingStatus";
import { ApiErrorResponse } from "../../api/types/ApiErrorResponse";
import {
  fetchMembers,
  fetchMemberById,
  createMember,
  updateMember,
  assignMemberInstructor,
  deleteMember,
  eraseMember,
  fetchGroups,
  fetchGroupById,
  createGroup,
  updateGroup,
  deleteGroup,
  addMemberToGroup,
  removeMemberFromGroup,
  fetchGroupMembers,
} from "./slice-extensions/members-slice-extension";
import { setMemberMessagesStopped } from "./slice-extensions/member-messages-stop-slice-extension";

/**
 * Interface defining the members feature state
 */
export interface MembersState {
  members: Member[];
  groups: Group[];
  currentMember: Member | undefined;
  currentGroup: Group | undefined;
  status: LoadingStatus;
  groupsStatus: LoadingStatus;
  statusMessage: string | undefined;
  initializationStatus: "initialized" | "initializing" | "not-initialized" | "failed";
  groupsInitializationStatus: "initialized" | "initializing" | "not-initialized" | "failed";
  selectedTab: "all" | "individuals" | "groups";
}

/**
 * Initial state for the members slice
 */
const initialState: MembersState = {
  members: [],
  groups: [],
  currentMember: undefined,
  currentGroup: undefined,
  status: LoadingStatus.Idle,
  groupsStatus: LoadingStatus.Idle,
  statusMessage: undefined,
  initializationStatus: "not-initialized",
  groupsInitializationStatus: "not-initialized",
  selectedTab: "all",
};

/**
 * Members slice containing reducers and actions
 */
const membersSlice = createSlice({
  name: "members",
  initialState,
  reducers: {
    /**
     * Clears any error messages from the state
     */
    clearError: (state) => {
      state.statusMessage = undefined;
      if (state.status === LoadingStatus.Failed) {
        state.status = LoadingStatus.Idle;
      }
      if (state.groupsStatus === LoadingStatus.Failed) {
        state.groupsStatus = LoadingStatus.Idle;
      }
    },
    /**
     * Sets the current member for detail/edit operations
     */
    setCurrentMember: (state, action: PayloadAction<Member | undefined>) => {
      state.currentMember = action.payload;
    },
    /**
     * Clears the current member
     */
    clearCurrentMember: (state) => {
      state.currentMember = undefined;
    },
    /**
     * Sets the current group for detail/edit operations
     */
    setCurrentGroup: (state, action: PayloadAction<Group | undefined>) => {
      state.currentGroup = action.payload;
    },
    /**
     * Clears the current group
     */
    clearCurrentGroup: (state) => {
      state.currentGroup = undefined;
    },
    /**
     * Resets the members state to initial state
     */
    resetMembers: () => initialState,
    /**
     * Sets the selected tab in the members view
     */
    setSelectedTab: (
      state,
      action: PayloadAction<"all" | "individuals" | "groups">
    ) => {
      state.selectedTab = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Fetch members cases
    builder
      .addCase(fetchMembers.pending, (state) => {
        state.status = LoadingStatus.Loading;
        state.statusMessage = undefined;
        if (state.initializationStatus === "not-initialized" || state.initializationStatus === "failed") {
          state.initializationStatus = "initializing";
        }
      })
      .addCase(
        fetchMembers.fulfilled,
        (state, action: PayloadAction<Member[]>) => {
          state.status = LoadingStatus.Idle;
          state.statusMessage = undefined;
          state.members = action.payload;
          if (state.initializationStatus === "initializing") {
            state.initializationStatus = "initialized";
          }
        }
      )
      .addCase(fetchMembers.rejected, (state, action) => {
        state.status = LoadingStatus.Failed;
        const error = action.payload as ApiErrorResponse;
        state.statusMessage = error?.message || "Failed to fetch members";
        // "failed", not "not-initialized" — reverting would make any mount-effect
        // that keys on "not-initialized" refetch instantly, i.e. a request storm.
        if (state.initializationStatus === "initializing") {
          state.initializationStatus = "failed";
        }
      });

    // Fetch member by ID cases
    builder
      .addCase(fetchMemberById.pending, (state) => {
        state.status = LoadingStatus.Loading;
        state.statusMessage = undefined;
      })
      .addCase(
        fetchMemberById.fulfilled,
        (state, action: PayloadAction<Member>) => {
          state.status = LoadingStatus.Idle;
          state.statusMessage = undefined;
          state.currentMember = action.payload;
        }
      )
      .addCase(fetchMemberById.rejected, (state, action) => {
        state.status = LoadingStatus.Failed;
        const error = action.payload as ApiErrorResponse;
        state.statusMessage = error?.message || "Failed to fetch member";
      });

    // Create member cases
    builder
      .addCase(createMember.pending, (state) => {
        state.status = LoadingStatus.Loading;
        state.statusMessage = undefined;
      })
      .addCase(
        createMember.fulfilled,
        (state, action: PayloadAction<Member>) => {
          state.status = LoadingStatus.Idle;
          state.statusMessage = undefined;
          state.members.push(action.payload);
          state.currentMember = action.payload;
        }
      )
      .addCase(createMember.rejected, (state, action) => {
        state.status = LoadingStatus.Failed;
        const error = action.payload as ApiErrorResponse;
        state.statusMessage = error?.message || "Failed to create member";
      });

    // Update member cases
    builder
      .addCase(updateMember.pending, (state) => {
        state.status = LoadingStatus.Loading;
        state.statusMessage = undefined;
      })
      .addCase(
        updateMember.fulfilled,
        (state, action: PayloadAction<Member>) => {
          state.status = LoadingStatus.Idle;
          state.statusMessage = undefined;
          const index = state.members.findIndex(
            (member) => member.id === action.payload.id
          );
          if (index >= 0) {
            state.members[index] = action.payload;
          }
          if (
            state.currentMember &&
            state.currentMember.id === action.payload.id
          ) {
            state.currentMember = action.payload;
          }
        }
      )
      .addCase(setMemberMessagesStopped.fulfilled, (state, action) => {
        const index = state.members.findIndex((member) => member.id === action.payload.id);
        if (index >= 0) state.members[index] = action.payload;
        if (state.currentMember?.id === action.payload.id) state.currentMember = action.payload;
      })
      .addCase(updateMember.rejected, (state, action) => {
        state.status = LoadingStatus.Failed;
        const error = action.payload as ApiErrorResponse;
        state.statusMessage = error?.message || "Failed to update member";
      });

    // Assign instructor cases — the member's instructorId updates in place
    builder
      .addCase(assignMemberInstructor.pending, (state) => {
        state.status = LoadingStatus.Loading;
        state.statusMessage = undefined;
      })
      .addCase(
        assignMemberInstructor.fulfilled,
        (state, action: PayloadAction<Member>) => {
          state.status = LoadingStatus.Idle;
          state.statusMessage = undefined;
          const index = state.members.findIndex(
            (member) => member.id === action.payload.id
          );
          if (index >= 0) {
            state.members[index] = action.payload;
          }
          if (
            state.currentMember &&
            state.currentMember.id === action.payload.id
          ) {
            state.currentMember = action.payload;
          }
        }
      )
      .addCase(assignMemberInstructor.rejected, (state, action) => {
        state.status = LoadingStatus.Failed;
        const error = action.payload as ApiErrorResponse;
        state.statusMessage = error?.message || "Failed to assign instructor";
      });

    // Delete member cases
    builder
      .addCase(deleteMember.pending, (state) => {
        state.status = LoadingStatus.Loading;
        state.statusMessage = undefined;
      })
      .addCase(
        deleteMember.fulfilled,
        (state, action: PayloadAction<string>) => {
          state.status = LoadingStatus.Idle;
          state.statusMessage = undefined;
          state.members = state.members.filter(
            (member) => member.id !== action.payload
          );
          if (
            state.currentMember &&
            state.currentMember.id === action.payload
          ) {
            state.currentMember = undefined;
          }
        }
      )
      .addCase(deleteMember.rejected, (state, action) => {
        state.status = LoadingStatus.Failed;
        const error = action.payload as ApiErrorResponse;
        state.statusMessage = error?.message || "Failed to delete member";
      });

    // Erase member cases (right to be forgotten) — drop the member like a delete.
    builder
      .addCase(eraseMember.pending, (state) => {
        state.status = LoadingStatus.Loading;
        state.statusMessage = undefined;
      })
      .addCase(
        eraseMember.fulfilled,
        (state, action: PayloadAction<MemberErasureResult>) => {
          state.status = LoadingStatus.Idle;
          state.statusMessage = undefined;
          state.members = state.members.filter(
            (member) => member.id !== action.payload.memberId
          );
          if (
            state.currentMember &&
            state.currentMember.id === action.payload.memberId
          ) {
            state.currentMember = undefined;
          }
        }
      )
      .addCase(eraseMember.rejected, (state, action) => {
        state.status = LoadingStatus.Failed;
        const error = action.payload as ApiErrorResponse;
        state.statusMessage = error?.message || "Failed to erase member data";
      });

    // Fetch groups cases
    builder
      .addCase(fetchGroups.pending, (state) => {
        state.groupsStatus = LoadingStatus.Loading;
        state.statusMessage = undefined;
        if (state.groupsInitializationStatus === "not-initialized" || state.groupsInitializationStatus === "failed") {
          state.groupsInitializationStatus = "initializing";
        }
      })
      .addCase(
        fetchGroups.fulfilled,
        (state, action: PayloadAction<Group[]>) => {
          state.groupsStatus = LoadingStatus.Idle;
          state.statusMessage = undefined;
          state.groups = action.payload;
          if (state.groupsInitializationStatus === "initializing") {
            state.groupsInitializationStatus = "initialized";
          }
        }
      )
      .addCase(fetchGroups.rejected, (state, action) => {
        state.groupsStatus = LoadingStatus.Failed;
        const error = action.payload as ApiErrorResponse;
        state.statusMessage = error?.message || "Failed to fetch groups";
        // "failed", not "not-initialized" — see fetchMembers.rejected.
        if (state.groupsInitializationStatus === "initializing") {
          state.groupsInitializationStatus = "failed";
        }
      });

    // Fetch group by ID cases
    builder
      .addCase(fetchGroupById.pending, (state) => {
        state.groupsStatus = LoadingStatus.Loading;
        state.statusMessage = undefined;
      })
      .addCase(
        fetchGroupById.fulfilled,
        (state, action: PayloadAction<Group>) => {
          state.groupsStatus = LoadingStatus.Idle;
          state.statusMessage = undefined;
          state.currentGroup = action.payload;
        }
      )
      .addCase(fetchGroupById.rejected, (state, action) => {
        state.groupsStatus = LoadingStatus.Failed;
        const error = action.payload as ApiErrorResponse;
        state.statusMessage = error?.message || "Failed to fetch group";
      });

    // Create group cases
    builder
      .addCase(createGroup.pending, (state) => {
        state.groupsStatus = LoadingStatus.Loading;
        state.statusMessage = undefined;
      })
      .addCase(
        createGroup.fulfilled,
        (state, action: PayloadAction<Group>) => {
          state.groupsStatus = LoadingStatus.Idle;
          state.statusMessage = undefined;
          state.groups.push(action.payload);
          state.currentGroup = action.payload;
        }
      )
      .addCase(createGroup.rejected, (state, action) => {
        state.groupsStatus = LoadingStatus.Failed;
        const error = action.payload as ApiErrorResponse;
        state.statusMessage = error?.message || "Failed to create group";
      });

    // Update group cases
    builder
      .addCase(updateGroup.pending, (state) => {
        state.groupsStatus = LoadingStatus.Loading;
        state.statusMessage = undefined;
      })
      .addCase(
        updateGroup.fulfilled,
        (state, action: PayloadAction<Group>) => {
          state.groupsStatus = LoadingStatus.Idle;
          state.statusMessage = undefined;
          const index = state.groups.findIndex(
            (group) => group.id === action.payload.id
          );
          if (index >= 0) {
            state.groups[index] = action.payload;
          }
          if (
            state.currentGroup &&
            state.currentGroup.id === action.payload.id
          ) {
            state.currentGroup = action.payload;
          }
        }
      )
      .addCase(updateGroup.rejected, (state, action) => {
        state.groupsStatus = LoadingStatus.Failed;
        const error = action.payload as ApiErrorResponse;
        state.statusMessage = error?.message || "Failed to update group";
      });

    // Delete group cases
    builder
      .addCase(deleteGroup.pending, (state) => {
        state.groupsStatus = LoadingStatus.Loading;
        state.statusMessage = undefined;
      })
      .addCase(
        deleteGroup.fulfilled,
        (state, action: PayloadAction<string>) => {
          state.groupsStatus = LoadingStatus.Idle;
          state.statusMessage = undefined;
          state.groups = state.groups.filter(
            (group) => group.id !== action.payload
          );
          if (
            state.currentGroup &&
            state.currentGroup.id === action.payload
          ) {
            state.currentGroup = undefined;
          }
        }
      )
      .addCase(deleteGroup.rejected, (state, action) => {
        state.groupsStatus = LoadingStatus.Failed;
        const error = action.payload as ApiErrorResponse;
        state.statusMessage = error?.message || "Failed to delete group";
      });

    // Add member to group cases
    builder
      .addCase(addMemberToGroup.pending, (state) => {
        state.status = LoadingStatus.Loading;
        state.statusMessage = undefined;
      })
      .addCase(addMemberToGroup.fulfilled, (state) => {
        state.status = LoadingStatus.Idle;
        state.statusMessage = undefined;
      })
      .addCase(addMemberToGroup.rejected, (state, action) => {
        state.status = LoadingStatus.Failed;
        const error = action.payload as ApiErrorResponse;
        state.statusMessage =
          error?.message || "Failed to add member to group";
      });

    // Remove member from group cases
    builder
      .addCase(removeMemberFromGroup.pending, (state) => {
        state.status = LoadingStatus.Loading;
        state.statusMessage = undefined;
      })
      .addCase(removeMemberFromGroup.fulfilled, (state) => {
        state.status = LoadingStatus.Idle;
        state.statusMessage = undefined;
      })
      .addCase(removeMemberFromGroup.rejected, (state, action) => {
        state.status = LoadingStatus.Failed;
        const error = action.payload as ApiErrorResponse;
        state.statusMessage =
          error?.message || "Failed to remove member from group";
      });

    // Fetch group members cases (component handles result locally)
    builder
      .addCase(fetchGroupMembers.pending, (state) => {
        state.status = LoadingStatus.Loading;
        state.statusMessage = undefined;
      })
      .addCase(fetchGroupMembers.fulfilled, (state) => {
        state.status = LoadingStatus.Idle;
        state.statusMessage = undefined;
      })
      .addCase(fetchGroupMembers.rejected, (state, action) => {
        state.status = LoadingStatus.Failed;
        const error = action.payload as ApiErrorResponse;
        state.statusMessage =
          error?.message || "Failed to fetch group members";
      });
  },
});

export const { actions, reducer } = membersSlice;
export const {
  clearError,
  setCurrentMember,
  clearCurrentMember,
  setCurrentGroup,
  clearCurrentGroup,
  resetMembers,
  setSelectedTab,
} = actions;

// Selectors for accessing members state
export const selectMembers = (state: { members: MembersState }) =>
  state.members.members;
export const selectGroups = (state: { members: MembersState }) =>
  state.members.groups;
export const selectCurrentMember = (state: { members: MembersState }) =>
  state.members.currentMember;
export const selectCurrentGroup = (state: { members: MembersState }) =>
  state.members.currentGroup;
export const selectMembersStatus = (state: { members: MembersState }) =>
  state.members.status;
export const selectGroupsStatus = (state: { members: MembersState }) =>
  state.members.groupsStatus;
export const selectSelectedTab = (state: { members: MembersState }) =>
  state.members.selectedTab;
export const selectMembersInitializationStatus = (state: {
  members: MembersState;
}) => state.members.initializationStatus;
export const selectGroupsInitializationStatus = (state: {
  members: MembersState;
}) => state.members.groupsInitializationStatus;

export default reducer;
